/**
 * AFS Grain Fineness Number — TypeScript port of
 * platform-core/internal/qc/gfn.go. Keep the two in lockstep. A sieve test
 * weighs the sand retained on each standard mesh; the GFN summarises grain
 * size: GFN = Σ(multiplierᵢ·massᵢ) / Σ massᵢ  (AFS 1106). See
 * docs/versatile/specs/data-dashboards-qc.md §3.6.
 */

export interface SieveStep {
  meshDesignation: string;
  apertureMicron: number;
  afsMultiplier: number;
  order: number;
}

export interface GFNResult {
  gfn: number;
  /** mesh → retained percent (massᵢ / Σmass · 100). */
  retainedPct: Record<string, number>;
  /** Σ retained mass over the meshes with a positive mass. */
  total: number;
}

/** The standard US-series AFS set — mirrors packs/foundry/sieve-sets/afs_us_series.yaml. */
export const AFS_US_SERIES: SieveStep[] = [
  { meshDesignation: "6", apertureMicron: 3350, afsMultiplier: 3, order: 1 },
  { meshDesignation: "12", apertureMicron: 1700, afsMultiplier: 5, order: 2 },
  { meshDesignation: "20", apertureMicron: 850, afsMultiplier: 10, order: 3 },
  { meshDesignation: "30", apertureMicron: 600, afsMultiplier: 20, order: 4 },
  { meshDesignation: "40", apertureMicron: 425, afsMultiplier: 30, order: 5 },
  { meshDesignation: "50", apertureMicron: 300, afsMultiplier: 40, order: 6 },
  { meshDesignation: "70", apertureMicron: 212, afsMultiplier: 50, order: 7 },
  { meshDesignation: "100", apertureMicron: 150, afsMultiplier: 70, order: 8 },
  { meshDesignation: "140", apertureMicron: 106, afsMultiplier: 100, order: 9 },
  { meshDesignation: "200", apertureMicron: 75, afsMultiplier: 140, order: 10 },
  { meshDesignation: "270", apertureMicron: 53, afsMultiplier: 200, order: 11 },
  { meshDesignation: "pan", apertureMicron: 0, afsMultiplier: 300, order: 12 },
];

/**
 * GFN = Σ(Mᵢ·massᵢ)/Σmassᵢ over the meshes with a positive retained mass;
 * retainedPct[mesh] = massᵢ/Σmass·100. Mirrors ComputeGFN in gfn.go: zero /
 * negative masses are skipped. (Unlike the Go version it ignores a mesh not in
 * `set` rather than erroring, since the entry form only offers set meshes.)
 * Returns { gfn: 0, total: 0 } when there's no retained mass.
 */
export function computeGFN(set: SieveStep[], retained: Record<string, number>): GFNResult {
  const mult = new Map(set.map((s) => [s.meshDesignation, s.afsMultiplier]));
  let weighted = 0;
  let total = 0;
  for (const [mesh, mass] of Object.entries(retained)) {
    if (!(mass > 0)) continue;
    const m = mult.get(mesh);
    if (m === undefined) continue;
    weighted += m * mass;
    total += mass;
  }
  if (total <= 0) return { gfn: 0, retainedPct: {}, total: 0 };
  const retainedPct: Record<string, number> = {};
  for (const [mesh, mass] of Object.entries(retained)) {
    if (mass > 0) retainedPct[mesh] = (mass / total) * 100;
  }
  return { gfn: weighted / total, retainedPct, total };
}
